import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DecorativeManagement() {
  const { token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: 'decorative',
    word1: '',
    word1_en: '',
    word1_image: '',
    word2: '',
    word2_en: '',
    word2_image: ''
  });

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/admin/login');
      return;
    }
    if (!authLoading && token) {
      loadData();
    }
  }, [authLoading, token, navigate]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/home-content/decorative`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      console.error('Erro ao carregar:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar os dados"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/home-content/decorative`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Sucesso",
        description: "Seção decorativa atualizada com sucesso"
      });
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível salvar as alterações"
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Seção Decorativa
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Imagem 1 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Primeira Imagem
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="word1">
                  Texto Sobreposto (PT) 
                  <span className="text-xs text-gray-500 ml-2">(deixe vazio para não mostrar texto)</span>
                </Label>
                <Input
                  id="word1"
                  value={formData.word1}
                  onChange={(e) => handleChange('word1', e.target.value)}
                  placeholder="Ex: CONSULTORIA (ou deixe vazio)"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="word1_en">
                  Texto Sobreposto (EN)
                  <span className="text-xs text-gray-500 ml-2">(opcional)</span>
                </Label>
                <Input
                  id="word1_en"
                  value={formData.word1_en}
                  onChange={(e) => handleChange('word1_en', e.target.value)}
                  placeholder="Ex: CONSULTING"
                  className="mt-1"
                />
              </div>

              <ImageUpload
                label="Imagem (upload do computador)"
                value={formData.word1_image || ''}
                onChange={(url) => handleChange('word1_image', url)}
                required
              />

              {/* Preview */}
              {formData.word1_image && (
                <div className="mt-4">
                  <Label className="text-xs text-gray-500 mb-2 block">Preview:</Label>
                  <div className="relative w-48 h-64 rounded-lg overflow-hidden">
                    <img 
                      src={formData.word1_image} 
                      alt="Preview 1"
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.src = 'https://via.placeholder.com/400x600?text=Erro+ao+carregar'}
                    />
                    {formData.word1 && (
                      <div className="absolute top-4 left-4 right-4">
                        <span className="text-2xl font-bold text-white drop-shadow-lg">
                          {formData.word1}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Imagem 2 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Segunda Imagem
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="word2">
                  Texto Sobreposto (PT)
                  <span className="text-xs text-gray-500 ml-2">(deixe vazio para não mostrar texto)</span>
                </Label>
                <Input
                  id="word2"
                  value={formData.word2}
                  onChange={(e) => handleChange('word2', e.target.value)}
                  placeholder="Ex: CONFIANÇA (ou deixe vazio)"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="word2_en">
                  Texto Sobreposto (EN)
                  <span className="text-xs text-gray-500 ml-2">(opcional)</span>
                </Label>
                <Input
                  id="word2_en"
                  value={formData.word2_en}
                  onChange={(e) => handleChange('word2_en', e.target.value)}
                  placeholder="Ex: TRUST"
                  className="mt-1"
                />
              </div>

              <ImageUpload
                label="Imagem (upload do computador)"
                value={formData.word2_image || ''}
                onChange={(url) => handleChange('word2_image', url)}
                required
              />

              {/* Preview */}
              {formData.word2_image && (
                <div className="mt-4">
                  <Label className="text-xs text-gray-500 mb-2 block">Preview:</Label>
                  <div className="relative w-48 h-64 rounded-lg overflow-hidden">
                    <img 
                      src={formData.word2_image} 
                      alt="Preview 2"
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.src = 'https://via.placeholder.com/400x600?text=Erro+ao+carregar'}
                    />
                    {formData.word2 && (
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="text-2xl font-bold text-white drop-shadow-lg">
                          {formData.word2}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Informação */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <p className="text-sm text-blue-800">
                💡 <strong>Dica:</strong> Deixe os campos de texto vazios se quiser mostrar apenas as imagens, sem palavras sobrepostas.
              </p>
            </CardContent>
          </Card>

          {/* Botão Salvar */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/dashboard')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-[#2E5E99] hover:bg-[#0D2440] text-white flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Salvar Alterações
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
